import { useNavigate } from 'react-router-dom';
import { Swords, TrendingUp, Users, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { QRCodeSVG } from 'qrcode.react';

const NINERS_LOGO = "https://customer-assets.emergentagent.com/job_7442c566-20f0-4c0f-8d05-f273c41d0090/artifacts/usp3jo2t_DSV_Niners_Logo.png";
const RANKING_URL = typeof window !== 'undefined' ? window.location.origin : '';

function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Hero Section */}
      <div 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758341452005-15899b9d6e55?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(circle at 50% 0%, rgba(0, 122, 255, 0.15) 0%, transparent 70%)'
        }}></div>
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <div className="mb-8 animate-fade-in">
            <div className="inline-block p-4 sm:p-6 bg-white/10 backdrop-blur-xl rounded-3xl mb-6 hover:scale-105 transition-transform duration-300">
              <img 
                src={NINERS_LOGO}
                alt="DSV Niners"
                className="w-24 h-24 sm:w-40 sm:h-40 object-contain"
              />
            </div>
          </div>
          <h1 
            className="text-3xl sm:text-5xl lg:text-6xl font-semibold mb-4 sm:mb-6 tracking-tight animate-slide-up"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Compete. Rank. Dominate.
          </h1>
          <p className="text-base sm:text-xl text-gray-300 mb-8 sm:mb-12 max-w-2xl mx-auto animate-slide-up stagger-1">
            ELO-based tournament ranking system for competitive players. Track your performance, challenge opponents and climb the leaderboard.
          </p>
          <div className="flex gap-3 sm:gap-4 justify-center flex-wrap animate-slide-up stagger-2">
            <Button 
              data-testid="get-started-btn"
              onClick={() => navigate('/login')}
              className="bg-[#007AFF] hover:bg-[#0066D6] text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full font-bold tracking-wide shadow-lg shadow-[#007AFF]/20 hover-lift btn-press animate-pulse-glow"
            >
              Get Started
            </Button>
            <Button 
              onClick={() => navigate('/ranking-lists')}
              className="border-2 border-white/30 bg-white/5 hover:bg-white/10 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full font-bold backdrop-blur-sm hover-lift btn-press"
            >
              View Rankings
            </Button>
          </div>
          
          {/* Scroll indicator */}
          <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-6 h-6 sm:w-8 sm:h-8 text-white/50" />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 sm:py-24 px-4 bg-[#111111]">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-2xl sm:text-4xl font-semibold text-center mb-12 sm:mb-16"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Built for Competitors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8">
            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 sm:p-8 card-hover animate-slide-up stagger-1">
              <Swords className="w-10 h-10 sm:w-12 sm:h-12 text-[#007AFF] mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Challenge Opponents</h3>
              <p className="text-sm sm:text-base text-gray-400">Send match invites, enter scores, and confirm results with your opponents in real-time.</p>
            </div>
            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 sm:p-8 card-hover animate-slide-up stagger-2">
              <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-[#007AFF] mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>ELO Ranking System</h3>
              <p className="text-sm sm:text-base text-gray-400">Fair and accurate ranking algorithm that rewards skill and consistency over time.</p>
            </div>
            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 sm:p-8 card-hover animate-slide-up stagger-3">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 text-[#007AFF] mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Multiple Categories</h3>
              <p className="text-sm sm:text-base text-gray-400">Join different ranking lists for various games and compete across multiple categories.</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 sm:py-24 px-4 bg-gradient-to-b from-[#111111] to-[#0A0A0A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-2xl sm:text-4xl font-semibold mb-4 sm:mb-6"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Ready to Compete?
          </h2>
          <p className="text-base sm:text-xl text-gray-300 mb-6 sm:mb-8">
            Join now and start climbing the ranks
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12">
            <Button 
              data-testid="join-now-btn"
              onClick={() => navigate('/login')}
              className="bg-[#007AFF] hover:bg-[#0066D6] text-white px-12 py-6 text-xl rounded-full font-bold tracking-wide shadow-lg shadow-[#007AFF]/20 transition-all active:scale-95"
            >
              Join Now
            </Button>
            <div className="flex flex-col items-center gap-3">
              <div className="bg-white p-3 rounded-xl shadow-lg">
                <QRCodeSVG 
                  value={RANKING_URL}
                  size={120}
                  level="M"
                  includeMargin={false}
                />
              </div>
              <p className="text-sm text-gray-400">Scan to visit</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LandingPage;